/**
 * Real-time analyser (spec 26).
 *
 * Everything here is fed from the analyser tap's ring buffer, which is a
 * read-only copy of the audio. The visualisers are completely decoupled from
 * the DSP path: if the browser throttles this tab, if a canvas throws, or if
 * the ring buffer underruns, audio is unaffected. One requestAnimationFrame
 * loop drives every scope so there is a single, predictable cost.
 */

import { useEffect, useRef, useState } from 'react';
import { engine } from '../../audio/engine';
import { getFFT } from '../../dsp/fft';
import { useApp } from '../../stores/appStore';
import { Segmented } from './primitives';

const FFT_SIZE = 4096;
const RING_READ = 8192;

type ScopeMode = 'all' | 'spectrum' | 'spectrogram' | 'waveform' | 'vector';

export function Analyzer() {
  const [collapsed, setCollapsed] = useState(false);
  const [mode, setMode] = useState<ScopeMode>('all');
  const [holdPeak, setHoldPeak] = useState(true);
  const meters = useApp((s) => s.meters);
  const playing = useApp((s) => s.playing);

  const waveRef = useRef<HTMLCanvasElement>(null);
  const specRef = useRef<HTMLCanvasElement>(null);
  const sgRef = useRef<HTMLCanvasElement>(null);
  const vecRef = useRef<HTMLCanvasElement>(null);

  // Persistent scratch: allocating any of this per frame would cause visible GC.
  const scratch = useRef({
    ring: new Float32Array(RING_READ * 2),
    mono: new Float32Array(FFT_SIZE),
    re: new Float32Array(FFT_SIZE),
    im: new Float32Array(FFT_SIZE),
    mag: new Float32Array(FFT_SIZE / 2 + 1),
    smooth: new Float32Array(FFT_SIZE / 2 + 1),
    peakHold: new Float32Array(FFT_SIZE / 2 + 1),
    window: new Float32Array(FFT_SIZE),
    history: new Float32Array(FFT_SIZE),
    historyFill: 0,
    sgCol: 0,
    filled: false,
  });

  useEffect(() => {
    const s = scratch.current;
    for (let i = 0; i < FFT_SIZE; i++) {
      // Blackman-Harris: low sidelobes matter more than resolution here,
      // because the interesting thing is usually a low-level artefact next to a
      // high-level fundamental.
      const t = (2 * Math.PI * i) / (FFT_SIZE - 1);
      s.window[i] = 0.35875 - 0.48829 * Math.cos(t) + 0.14128 * Math.cos(2 * t) - 0.01168 * Math.cos(3 * t);
    }
    s.smooth.fill(-140);
    s.peakHold.fill(-140);
  }, []);

  useEffect(() => {
    if (collapsed) return;
    let raf = 0;
    const s = scratch.current;

    const dpr = () => Math.min(window.devicePixelRatio || 1, 2);
    const prep = (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      const d = dpr();
      if (canvas.width !== w * d || canvas.height !== h * d) {
        canvas.width = w * d;
        canvas.height = h * d;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.setTransform(d, 0, 0, d, 0, 0);
      return { ctx, w, h };
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const got = engine.readRing(s.ring);
      const frames = got > 0 ? got : 0;

      if (frames > 0) {
        // Append to the rolling history used by the FFT.
        const need = Math.min(frames, FFT_SIZE);
        const keep = FFT_SIZE - need;
        s.history.copyWithin(0, need);
        for (let i = 0; i < need; i++) {
          const idx = (frames - need + i) * 2;
          s.history[keep + i] = (s.ring[idx] + s.ring[idx + 1]) * 0.5;
        }
        s.historyFill = Math.min(FFT_SIZE, s.historyFill + need);
        s.filled = s.historyFill >= FFT_SIZE;
      }

      // ---- Spectrum -----------------------------------------------------
      if (s.filled) {
        for (let i = 0; i < FFT_SIZE; i++) {
          s.re[i] = s.history[i] * s.window[i];
          s.im[i] = 0;
        }
        getFFT(FFT_SIZE).forward(s.re, s.im);
        const norm = 2 / (FFT_SIZE * 0.35875);
        for (let i = 0; i <= FFT_SIZE / 2; i++) {
          const m = Math.hypot(s.re[i], s.im[i]) * norm;
          const db = 20 * Math.log10(Math.max(1e-9, m));
          s.mag[i] = db;
          // Asymmetric smoothing: fast attack so transients show, slow release
          // so the eye can follow.
          s.smooth[i] = db > s.smooth[i] ? db : s.smooth[i] * 0.82 + db * 0.18;
          if (holdPeak) {
            s.peakHold[i] = db > s.peakHold[i] ? db : s.peakHold[i] - 0.12;
          }
        }
      }

      const fs = engine.sampleRate || 48000;
      const fMin = 20;
      const fMax = Math.min(fs / 2, 24000);

      if (mode === 'all' || mode === 'spectrum') {
        const p = prep(specRef.current);
        if (p) drawSpectrum(p.ctx, p.w, p.h, s.smooth, holdPeak ? s.peakHold : null, fs, fMin, fMax);
      }
      if (mode === 'all' || mode === 'spectrogram') {
        const p = prep(sgRef.current);
        if (p) {
          s.sgCol = drawSpectrogram(p.ctx, p.w, p.h, s.mag, fs, s.sgCol, s.filled);
        }
      }
      if (mode === 'all' || mode === 'waveform') {
        const p = prep(waveRef.current);
        if (p) drawWaveform(p.ctx, p.w, p.h, s.ring, frames);
      }
      if (mode === 'all' || mode === 'vector') {
        const p = prep(vecRef.current);
        if (p) drawVector(p.ctx, p.w, p.h, s.ring, frames);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [collapsed, mode, holdPeak, playing]);

  const show = (m: ScopeMode) => mode === 'all' || mode === m;

  return (
    <div className="analyzer">
      <div className="analyzer-head">
        <button className="btn sm" onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed}>
          {collapsed ? '▸' : '▾'} Analyser
        </button>
        {!collapsed && (
          <>
            <Segmented
              small
              ariaLabel="Analyser view"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'all', label: 'All' },
                { value: 'spectrum', label: 'Spectrum' },
                { value: 'spectrogram', label: 'Spectrogram' },
                { value: 'waveform', label: 'Wave' },
                { value: 'vector', label: 'Vector' },
              ]}
            />
            <button className={`btn sm${holdPeak ? ' on' : ''}`} onClick={() => setHoldPeak((h) => !h)}>
              Peak hold
            </button>
          </>
        )}
        <div className="spacer" />
        {meters && (
          <div className="mono tiny dim" style={{ display: 'flex', gap: 14 }}>
            <span title="Integrated loudness, ITU-R BS.1770-4 gated">
              LUFS-I <b className="bright">{fmtLufs(meters.integrated)}</b>
            </span>
            <span title="Momentary loudness, 400 ms window">
              M <b className="bright">{fmtLufs(meters.momentary)}</b>
            </span>
            <span title="Peak-to-loudness ratio of what is currently audible">
              PLR <b className="bright">{Number.isFinite(meters.integrated) ? (Math.max(meters.peakHoldL, meters.peakHoldR) - meters.integrated).toFixed(1) : '--'}</b>
            </span>
            <span title="Interchannel correlation: +1 mono, 0 uncorrelated, -1 out of phase">
              CORR <b className={meters.correlation < -0.2 ? 'dangerc' : 'bright'}>{meters.correlation.toFixed(2)}</b>
            </span>
            <span title="Samples that reached full scale at the output">
              CLIP <b className={meters.clipCount > 0 ? 'dangerc' : 'bright'}>{meters.clipCount}</b>
            </span>
            <span className="faint" title={meters.usingSab ? 'Lock-free SharedArrayBuffer transport' : 'postMessage transport (page is not cross-origin isolated)'}>
              {meters.usingSab ? 'SAB' : 'MSG'}
            </span>
          </div>
        )}
      </div>
      <div className={`analyzer-body${collapsed ? ' collapsed' : ''}`}>
        {show('spectrum') && (
          <div className="scope" style={{ flex: 2 }}>
            <span className="label">Spectrum</span>
            <canvas ref={specRef} />
          </div>
        )}
        {show('spectrogram') && (
          <div className="scope" style={{ flex: 2 }}>
            <span className="label">Spectrogram</span>
            <canvas ref={sgRef} />
          </div>
        )}
        {show('waveform') && (
          <div className="scope" style={{ flex: 1.4 }}>
            <span className="label">Waveform</span>
            <canvas ref={waveRef} />
          </div>
        )}
        {show('vector') && (
          <div className="scope" style={{ flex: '0 0 190px' }}>
            <span className="label">Vectorscope</span>
            <canvas ref={vecRef} />
          </div>
        )}
      </div>
    </div>
  );
}

const fmtLufs = (v: number) => (Number.isFinite(v) ? v.toFixed(1) : '--');

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

const TICKS = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
const TICK_LABEL: Record<number, string> = {
  20: '20', 50: '50', 100: '100', 200: '200', 500: '500',
  1000: '1k', 2000: '2k', 5000: '5k', 10000: '10k', 20000: '20k',
};

function drawSpectrum(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  mag: Float32Array,
  peak: Float32Array | null,
  fs: number,
  fMin: number,
  fMax: number,
) {
  ctx.clearRect(0, 0, w, h);
  const padB = 12;
  const ph = h - padB;
  const dbMin = -120;
  const dbMax = 6;
  const xOf = (f: number) => ((Math.log(f) - Math.log(fMin)) / (Math.log(fMax) - Math.log(fMin))) * w;
  const yOf = (db: number) => ph - ((db - dbMin) / (dbMax - dbMin)) * ph;

  ctx.strokeStyle = '#151b22';
  ctx.lineWidth = 1;
  for (let db = 0; db >= dbMin; db -= 20) {
    const y = Math.round(yOf(db)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  ctx.font = '8px ui-monospace, monospace';
  ctx.fillStyle = '#4d5763';
  ctx.textAlign = 'center';
  for (const f of TICKS) {
    if (f < fMin || f > fMax) continue;
    const x = Math.round(xOf(f)) + 0.5;
    ctx.strokeStyle = '#151b22';
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, ph);
    ctx.stroke();
    ctx.fillText(TICK_LABEL[f], x, h - 3);
  }

  const bin = (f: number) => Math.round((f / fs) * (mag.length - 1) * 2);

  if (peak) {
    ctx.strokeStyle = '#3a4653';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = 0; x < w; x++) {
      const f = fMin * Math.pow(fMax / fMin, x / (w - 1));
      const i = Math.min(peak.length - 1, Math.max(0, bin(f)));
      const y = yOf(peak[i]);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  ctx.beginPath();
  for (let x = 0; x < w; x++) {
    const f = fMin * Math.pow(fMax / fMin, x / (w - 1));
    const i = Math.min(mag.length - 1, Math.max(0, bin(f)));
    const y = yOf(mag[i]);
    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = '#45c8b8';
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.lineTo(w, ph);
  ctx.lineTo(0, ph);
  ctx.closePath();
  ctx.fillStyle = 'rgba(69, 200, 184, 0.10)';
  ctx.fill();
}

function drawSpectrogram(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  mag: Float32Array,
  fs: number,
  col: number,
  ready: boolean,
): number {
  if (!ready) return col;
  // Scroll left by one column, then draw the newest column on the right.
  ctx.drawImage(ctx.canvas, -Math.min(window.devicePixelRatio || 1, 2), 0);
  const x = w - 1;
  const fMin = 20;
  const fMax = Math.min(fs / 2, 24000);
  for (let y = 0; y < h; y++) {
    const f = fMin * Math.pow(fMax / fMin, 1 - y / h);
    const i = Math.min(mag.length - 1, Math.max(0, Math.round((f / fs) * (mag.length - 1) * 2)));
    const db = mag[i];
    ctx.fillStyle = heat(db);
    ctx.fillRect(x, y, 1, 1);
  }
  return col + 1;
}

/** Perceptually monotone heat ramp from -110 dB to 0 dB. */
function heat(db: number): string {
  const t = Math.max(0, Math.min(1, (db + 110) / 110));
  if (t < 0.25) {
    const u = t / 0.25;
    return `rgb(${Math.round(8 + u * 8)},${Math.round(10 + u * 26)},${Math.round(14 + u * 60)})`;
  }
  if (t < 0.5) {
    const u = (t - 0.25) / 0.25;
    return `rgb(${Math.round(16 + u * 10)},${Math.round(36 + u * 100)},${Math.round(74 + u * 60)})`;
  }
  if (t < 0.75) {
    const u = (t - 0.5) / 0.25;
    return `rgb(${Math.round(26 + u * 190)},${Math.round(136 + u * 70)},${Math.round(134 - u * 90)})`;
  }
  const u = (t - 0.75) / 0.25;
  return `rgb(${Math.round(216 + u * 39)},${Math.round(206 + u * 45)},${Math.round(44 + u * 150)})`;
}

function drawWaveform(ctx: CanvasRenderingContext2D, w: number, h: number, ring: Float32Array, frames: number) {
  ctx.clearRect(0, 0, w, h);
  ctx.strokeStyle = '#232b36';
  ctx.beginPath();
  ctx.moveTo(0, Math.round(h / 2) + 0.5);
  ctx.lineTo(w, Math.round(h / 2) + 0.5);
  ctx.stroke();
  if (frames <= 1) return;

  const half = h / 2;
  for (const [ch, color] of [[0, '#45c8b8'], [1, '#6ea8ff']] as const) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.globalAlpha = ch === 0 ? 0.95 : 0.7;
    ctx.beginPath();
    const per = Math.max(1, Math.floor(frames / w));
    for (let x = 0; x < w; x++) {
      let min = 1;
      let max = -1;
      const start = Math.floor((x / w) * frames);
      for (let i = 0; i < per; i++) {
        const v = ring[(start + i) * 2 + ch] ?? 0;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      ctx.moveTo(x + 0.5, half - max * half * 0.94);
      ctx.lineTo(x + 0.5, half - min * half * 0.94);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawVector(ctx: CanvasRenderingContext2D, w: number, h: number, ring: Float32Array, frames: number) {
  // Fade rather than clear: a short persistence makes the pattern readable.
  ctx.fillStyle = 'rgba(8, 9, 11, 0.30)';
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) / 2 - 6;

  ctx.strokeStyle = '#1b222b';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - r, cy);
  ctx.lineTo(cx + r, cy);
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx, cy + r);
  ctx.stroke();
  // Mono and out-of-phase axes.
  ctx.strokeStyle = '#232b36';
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.707, cy + r * 0.707);
  ctx.lineTo(cx + r * 0.707, cy - r * 0.707);
  ctx.stroke();
  ctx.setLineDash([]);

  if (frames <= 0) return;
  ctx.fillStyle = 'rgba(69, 200, 184, 0.55)';
  const step = Math.max(1, Math.floor(frames / 1500));
  for (let i = 0; i < frames; i += step) {
    const l = ring[i * 2];
    const rr = ring[i * 2 + 1];
    // Rotate 45 degrees: mono content sits on the vertical axis, which is the
    // convention every mastering vectorscope uses.
    const x = cx + ((l - rr) / Math.SQRT2) * r * 0.94;
    const y = cy - ((l + rr) / Math.SQRT2) * r * 0.94;
    ctx.fillRect(x, y, 1.2, 1.2);
  }
}
